import { FC, useMemo, useState, useEffect, useRef, useCallback } from "react";
import { tokens } from "@/tokens";
import {
  Box,
  Typography,
  Stack,
  CircularProgress,
  Autocomplete,
  TextField,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Checkbox,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  CheckBoxOutlineBlank,
  CheckBox as CheckBoxIcon,
} from "@mui/icons-material";
import ReactApexChart from "react-apexcharts";
import { useGetAverageUnitCostQuery, AverageUnitCostManufacturerOption, AverageUnitCostPlotPoint } from "@/services/dashboard/dashboard-api";
import { formatNumberWithSuffix, numberWithCommas, formatHTSCode } from "@/utils";
import { getCountryFlag } from "@/constants/country-flags";
import { ApexOptions } from "apexcharts";
import { format } from "date-fns";
import { DateRangeFilter } from "@/components";
import { Range } from "react-date-range";
import { tableCompactSx, tableContainerSx } from "@/components/table/table-styles";

interface UnitCostAnalysisSectionProps {
  htsCode: string;
  defaultOrigin?: string;
  clientId?: string;
  htsCodeFormatted?: string;
  htsDescription?: string;
  isDescriptionLoading?: boolean;
  embedded?: boolean;
}

const UnitCostAnalysisSection: FC<UnitCostAnalysisSectionProps> = ({
  htsCode,
  defaultOrigin,
  clientId,
  htsCodeFormatted,
  htsDescription,
  isDescriptionLoading,
  embedded = false,
}) => {
  const [selectedOrigins, setSelectedOrigins] = useState<string[]>([]);
  const [selectedManufacturers, setSelectedManufacturers] = useState<AverageUnitCostManufacturerOption[]>([]);
  const [selectedEntries, setSelectedEntries] = useState<AverageUnitCostPlotPoint[]>([]);

  const [dateFilter, setDateFilter] = useState<Pick<Range, "startDate" | "endDate">>({
    startDate: new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
    endDate: new Date()
  });

  useEffect(() => {
    if (defaultOrigin && selectedOrigins.length === 0) {
      setSelectedOrigins([defaultOrigin]);
    }
  }, [defaultOrigin]);

  useEffect(() => {
    setSelectedEntries([]);
  }, [htsCode, selectedOrigins, selectedManufacturers, dateFilter]);

  const { data: response, isFetching } = useGetAverageUnitCostQuery({
    input: {
      htsCode: htsCode.replace(/\./g, ''),
      clientId,
      originCodes: selectedOrigins,
      manufacturerIds: selectedManufacturers.map(m => m.manufacturerID),
      dateFrom: dateFilter.startDate?.toISOString(),
      dateTo: dateFilter.endDate?.toISOString(),
      includeOptions: true
    }
  }, {
    skip: !htsCode
  });

  const data = response?.data;

  const plotDataRef = useRef<AverageUnitCostPlotPoint[]>([]);
  const selectedEntriesRef = useRef<AverageUnitCostPlotPoint[]>([]);

  useEffect(() => {
    plotDataRef.current = data?.plotData || [];
  }, [data?.plotData]);

  useEffect(() => {
    selectedEntriesRef.current = selectedEntries;
  }, [selectedEntries]);

  const handleMarkerClick = useCallback((seriesIndex: number, dataPointIndex: number) => {
    if (seriesIndex !== 1) return;

    const currentPlotData = plotDataRef.current;
    const clickedEntry = currentPlotData[dataPointIndex];
    if (!clickedEntry) return;

    const currentSelected = selectedEntriesRef.current;
    const isAlreadySelected = currentSelected.some(
      e => e.entryLine === clickedEntry.entryLine ||
           (e.date === clickedEntry.date && e.unitCost === clickedEntry.unitCost)
    );

    if (!isAlreadySelected) {
      setSelectedEntries(prev => [...prev, clickedEntry]);
    }
  }, []);

  const handleRemoveEntry = useCallback((entry: AverageUnitCostPlotPoint) => {
    setSelectedEntries(prev => prev.filter(
      e => !(e.entryLine === entry.entryLine ||
             (e.date === entry.date && e.unitCost === entry.unitCost))
    ));
  }, []);

  const chartOptions: ApexOptions = useMemo(() => ({
    chart: {
      height: 350,
      type: 'line',
      toolbar: { show: false },
      zoom: { enabled: true },
      events: {
        markerClick: (_event: unknown, _chartContext: unknown, opts: { seriesIndex: number; dataPointIndex: number }) => {
          handleMarkerClick(opts.seriesIndex, opts.dataPointIndex);
        }
      }
    },
    stroke: {
      width: [3, 0],
      curve: 'smooth'
    },
    fill: {
      type: ['solid', 'solid'],
      opacity: [1, 0.5]
    },
    markers: {
      size: [0, 8],
      colors: [tokens.brand.primary, '#1CB4BA'],
      strokeColors: '#fff',
      strokeWidth: 2,
      hover: {
        size: 10,
        sizeOffset: 3
      },
      showNullDataPoints: false
    },
    states: {
      hover: {
        filter: {
          type: 'lighten',
          value: 0.15
        }
      },
      active: {
        allowMultipleDataPointsSelection: true,
        filter: {
          type: 'darken',
          value: 0.35
        }
      }
    },
    colors: [tokens.brand.primary, '#1CB4BA'],
    xaxis: {
      type: 'datetime',
      labels: {
        datetimeFormatter: {
          year: 'yyyy',
          month: 'MMM \'yy',
          day: 'dd MMM',
          hour: 'HH:mm'
        },
        style: { fontFamily: 'Inter' }
      },
      tooltip: { enabled: false }
    },
    yaxis: {
      title: {
        text: 'Unit Cost ($)',
        style: { fontFamily: 'Inter', fontWeight: tokens.fontWeight.semibold }
      },
      labels: {
        formatter: (val) => formatNumberWithSuffix(val),
        style: { fontFamily: 'Inter' }
      }
    },
    tooltip: {
      shared: false,
      intersect: true,
      custom: function({ seriesIndex, dataPointIndex, w }) {
        const point = w.config.series[seriesIndex].data[dataPointIndex];
        const val = point.y;

        if (seriesIndex === 0) {
            return (
                '<div class="arrow_box" style="padding: 8px; background: white; border: 1px solid #ccc; border-radius: 4px;">' +
                '<span>' + format(new Date(point.x), 'MMM yyyy') + '</span><br>' +
                '<b>Avg Cost: $' + formatNumberWithSuffix(val) + '</b>' +
                '</div>'
            );
        } else {
            return (
                '<div class="arrow_box" style="padding: 8px; background: white; border: 1px solid #ccc; border-radius: 4px;">' +
                '<span>' + format(new Date(point.x), 'MMM d, yyyy') + '</span><br>' +
                '<b>Unit Cost: $' + formatNumberWithSuffix(val) + '</b>' +
                '</div>'
            );
        }
      }
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      fontFamily: 'Inter'
    },
    grid: {
      borderColor: '#f1f1f1'
    }
  }), [handleMarkerClick]);

  const series = useMemo(() => {
    if (!data) return [];

    const lineData = (data.chartData || []).map(d => ({
      x: new Date(d.year, d.month - 1, 1).getTime(),
      y: d.averageUnitCost
    }));

    const scatterData = (data.plotData || []).map(d => ({
      x: new Date(d.date).getTime(),
      y: d.unitCost
    }));

    return [
      {
        name: 'Avg Unit Cost (Monthly)',
        type: 'line',
        data: lineData
      },
      {
        name: 'Individual Shipments',
        type: 'scatter',
        data: scatterData
      }
    ];
  }, [data]);

  if (!htsCode) return null;

  const header = (
    <>
      {/* Header: HTS code + description + metrics */}
      <Box sx={{ px: 3, pt: embedded ? 0 : 3, pb: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <Chip
            label={htsCodeFormatted || formatHTSCode(htsCode)}
            sx={{
              fontFamily: 'monospace',
              fontWeight: tokens.fontWeight.heavy,
              fontSize: tokens.fontSize.md,
              bgcolor: 'rgba(11,73,75,0.08)',
              color: tokens.brand.primary,
              height: 28,
              borderRadius: tokens.radius.sm,
              '& .MuiChip-label': { px: 1.5 },
            }}
          />
          {isDescriptionLoading ? (
            <CircularProgress size={14} sx={{ color: tokens.brand.primary }} />
          ) : htsDescription ? (
            <Tooltip title={htsDescription} placement="top" arrow>
              <Typography sx={{
                fontSize: tokens.fontSize.md,
                color: tokens.text.secondary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                minWidth: 0,
              }}>
                {htsDescription}
              </Typography>
            </Tooltip>
          ) : null}
        </Stack>

        {data && (
          <Stack direction="row" spacing={4}>
            <Box>
              <Typography sx={{ fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.bold, textTransform: 'uppercase', color: tokens.text.muted, letterSpacing: '0.05em' }}>
                Avg Unit Cost
              </Typography>
              <Typography sx={{ fontSize: '1.25rem', fontWeight: tokens.fontWeight.heavy, color: tokens.brand.primary }}>
                ${formatNumberWithSuffix(data.averageUnitCost)}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ borderColor: tokens.border.default }} />
            <Box>
              <Typography sx={{ fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.bold, textTransform: 'uppercase', color: tokens.text.muted, letterSpacing: '0.05em' }}>
                Total Quantity
              </Typography>
              <Typography sx={{ fontSize: '1.25rem', fontWeight: tokens.fontWeight.heavy, color: tokens.text.primary }}>
                {formatNumberWithSuffix(data.totalQuantity)}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ borderColor: tokens.border.default }} />
            <Box>
              <Typography sx={{ fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.bold, textTransform: 'uppercase', color: tokens.text.muted, letterSpacing: '0.05em' }}>
                Total Value
              </Typography>
              <Typography sx={{ fontSize: '1.25rem', fontWeight: tokens.fontWeight.heavy, color: tokens.text.primary }}>
                ${formatNumberWithSuffix(data.totalGoodsValue)}
              </Typography>
            </Box>
          </Stack>
        )}
      </Box>

      <Divider sx={{ borderColor: tokens.border.default }} />
    </>
  );

  const content = (
    <Stack spacing={3} sx={{ p: 3 }}>
      {/* Date filter row */}
      <Stack direction="row" justifyContent="flex-end" alignItems="center">
        <DateRangeFilter value={dateFilter} onSelectDate={setDateFilter} />
      </Stack>

        {/* Filters */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ mb: 1, color: tokens.text.primary, fontWeight: tokens.fontWeight.semibold, fontSize: tokens.fontSize.base }}>
              Origins
            </Typography>
            <Autocomplete
              multiple
              size="small"
              options={data?.availableOriginCodes || []}
              value={selectedOrigins}
              onChange={(_, newValue) => setSelectedOrigins(newValue)}
              disableCloseOnSelect
              renderOption={(props, option, { selected }) => (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: '6px !important',
                    px: '12px !important',
                    borderBottom: `1px solid ${tokens.border.light}`,
                    '&:last-child': { borderBottom: 'none' },
                    '&:hover': { bgcolor: `${tokens.bg.hover} !important` },
                    ...(selected && { bgcolor: 'rgba(11,73,75,0.04) !important' }),
                  }}
                >
                  <Checkbox
                    icon={<CheckBoxOutlineBlank sx={{ fontSize: 18 }} />}
                    checkedIcon={<CheckBoxIcon sx={{ fontSize: 18 }} />}
                    checked={selected}
                    size="small"
                    sx={{ p: 0, color: tokens.text.disabled, '&.Mui-checked': { color: tokens.brand.primary } }}
                  />
                  <span style={{ fontSize: tokens.fontSize.xl, lineHeight: 1 }}>{getCountryFlag(option)}</span>
                  <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: selected ? tokens.fontWeight.semibold : tokens.fontWeight.normal, color: tokens.text.body }}>
                    {option}
                  </Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={selectedOrigins.length === 0 ? "Select origins..." : ""}
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: tokens.radius.md },
                    '& .MuiInputBase-input': { fontSize: tokens.fontSize.base },
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={`${getCountryFlag(option)} ${option}`}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: tokens.fontSize.xs,
                      fontWeight: tokens.fontWeight.semibold,
                      bgcolor: 'rgba(11,73,75,0.08)',
                      color: tokens.brand.primary,
                      borderRadius: tokens.radius.sm,
                      '& .MuiChip-deleteIcon': {
                        fontSize: 14,
                        color: tokens.brand.primary,
                        '&:hover': { color: tokens.brand.primaryHover },
                      },
                    }}
                  />
                ))
              }
              ListboxProps={{ style: { maxHeight: 240, padding: 0 } }}
              loading={isFetching}
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography sx={{ mb: 1, color: tokens.text.primary, fontWeight: tokens.fontWeight.semibold, fontSize: tokens.fontSize.base }}>
              Manufacturers
            </Typography>
            <Autocomplete
              multiple
              size="small"
              options={data?.availableManufacturers || []}
              getOptionLabel={(option) => option.manufacturerName || option.manufacturerID}
              value={selectedManufacturers}
              onChange={(_, newValue) => setSelectedManufacturers(newValue)}
              isOptionEqualToValue={(option, value) => option.manufacturerID === value.manufacturerID}
              disableCloseOnSelect
              renderOption={(props, option, { selected }) => (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: '6px !important',
                    px: '12px !important',
                    borderBottom: `1px solid ${tokens.border.light}`,
                    '&:last-child': { borderBottom: 'none' },
                    '&:hover': { bgcolor: `${tokens.bg.hover} !important` },
                    ...(selected && { bgcolor: 'rgba(11,73,75,0.04) !important' }),
                  }}
                >
                  <Checkbox
                    icon={<CheckBoxOutlineBlank sx={{ fontSize: 18 }} />}
                    checkedIcon={<CheckBoxIcon sx={{ fontSize: 18 }} />}
                    checked={selected}
                    size="small"
                    sx={{ p: 0, color: tokens.text.disabled, '&.Mui-checked': { color: tokens.brand.primary } }}
                  />
                  <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: selected ? tokens.fontWeight.semibold : tokens.fontWeight.normal, color: tokens.text.body, flex: 1 }}>
                    {option.manufacturerName || option.manufacturerID}
                  </Typography>
                  {option.value !== undefined && (
                    <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, fontFamily: 'monospace' }}>
                      ${formatNumberWithSuffix(option.value)}
                    </Typography>
                  )}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={selectedManufacturers.length === 0 ? "Select manufacturers..." : ""}
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: tokens.radius.md },
                    '& .MuiInputBase-input': { fontSize: tokens.fontSize.base },
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.manufacturerID}
                    label={option.manufacturerName || option.manufacturerID}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: tokens.fontSize.xs,
                      fontWeight: tokens.fontWeight.semibold,
                      bgcolor: 'rgba(11,73,75,0.08)',
                      color: tokens.brand.primary,
                      borderRadius: tokens.radius.sm,
                      '& .MuiChip-deleteIcon': {
                        fontSize: 14,
                        color: tokens.brand.primary,
                        '&:hover': { color: tokens.brand.primaryHover },
                      },
                    }}
                  />
                ))
              }
              ListboxProps={{ style: { maxHeight: 240, padding: 0 } }}
              loading={isFetching}
            />
          </Box>
        </Stack>

        {/* Chart */}
        <Box sx={{ minHeight: 350, position: 'relative' }}>
          {isFetching && (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(255,255,255,0.7)', zIndex: 1 }}
            >
              <CircularProgress />
            </Stack>
          )}

          {data && (series[0].data.length > 0 || series[1].data.length > 0) ? (
            <ReactApexChart
              options={chartOptions}
              series={series}
              type="line"
              height={350}
            />
          ) : !isFetching ? (
            <Stack alignItems="center" justifyContent="center" height="100%">
              <Typography sx={{ color: tokens.text.muted, fontSize: tokens.fontSize.md }}>
                No unit cost data available for the selected filters.
              </Typography>
            </Stack>
          ) : null}
        </Box>

        {/* Selected Entry Lines Table */}
        {selectedEntries.length > 0 && (
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Box>
                <Typography sx={{ fontSize: tokens.fontSize.md, fontWeight: tokens.fontWeight.bold, color: tokens.text.body }}>
                  Selected Entry Lines ({selectedEntries.length})
                </Typography>
                <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted }}>
                  Click on scatter plot points to add entry lines
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => setSelectedEntries([])}
                sx={{
                  color: tokens.text.secondary,
                  fontSize: tokens.fontSize.base,
                  fontWeight: tokens.fontWeight.semibold,

                  '&:hover': { bgcolor: tokens.bg.muted },
                }}
              >
                Clear All
              </Button>
            </Stack>
            <Box sx={{ ...tableContainerSx, maxHeight: 400, overflow: 'auto' }}>
              <Table size="small" stickyHeader sx={{ ...tableCompactSx }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Entry Summary + Line</TableCell>
                    <TableCell>HTS Code</TableCell>
                    <TableCell>Origin</TableCell>
                    <TableCell>Manufacturer</TableCell>
                    <TableCell align="right">Unit Cost</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Value</TableCell>
                    <TableCell align="right">Line Duties</TableCell>
                    <TableCell align="center"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedEntries.map((entry, index) => {
                    const lineDuties = (entry.mfnDuty || 0) + (entry.duty301 || 0) + (entry.duty232 || 0) + (entry.oth99Duties || 0);
                    return (
                      <TableRow key={entry.entryLine || `${entry.date}-${index}`}>
                        <TableCell>{format(new Date(entry.date), 'MMM d, yyyy')}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>
                          {entry.entryLine || '-'}
                        </TableCell>
                        <TableCell>{entry.htsNumberFull ? formatHTSCode(entry.htsNumberFull) : '-'}</TableCell>
                        <TableCell>{entry.countryOfOriginCode || '-'}</TableCell>
                        <TableCell>{entry.manufacturerID || '-'}</TableCell>
                        <TableCell align="right">${formatNumberWithSuffix(entry.unitCost)}</TableCell>
                        <TableCell align="right">{numberWithCommas(entry.quantity)}</TableCell>
                        <TableCell align="right">${formatNumberWithSuffix(entry.value)}</TableCell>
                        <TableCell align="right">
                          {lineDuties > 0 ? `$${formatNumberWithSuffix(lineDuties)}` : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveEntry(entry)}
                            sx={{ color: tokens.text.muted, '&:hover': { color: tokens.status.danger } }}
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}
    </Stack>
  );

  if (embedded) {
    return (
      <>
        {header}
        {content}
      </>
    );
  }

  return (
    <Box sx={{
      borderRadius: tokens.radius.lg,
      border: `1px solid ${tokens.border.container}`,
      bgcolor: tokens.bg.surface,
      boxShadow: tokens.shadow.sm,
    }}>
      {header}
      {content}
    </Box>
  );
};

export default UnitCostAnalysisSection;
